import type { MaterialInfo } from '@rogue/rackbuilder'
import type { RackOptions } from '../../../../RackTypes'
import { RACK_COLORS } from '../../../../constants'
import { BaseMaterialStrategy } from './baseMaterialStrategy'

export class RMLMaterialStrategy extends BaseMaterialStrategy {
  // RML nameplates never branch on back type, so the base signature's backOverride is left off here.
  createNameplateMaterials(nameplateOverride?: MaterialInfo): MaterialInfo {
    // If explicitly set, use that
    const nameplate = nameplateOverride ?? this.config.materials?.nameplate
    if (nameplate) {
      return nameplate
    }

    const materials = JSON.parse(JSON.stringify(this.createMainMaterial())) as MaterialInfo

    if (this.config.color === 'powdercoat') {
      // Both textured finishes (Textured Black + Textured Charcoal Gray/Textured Black) use satin black for the nameplate crossmember.
      materials.main = { name: 'metal', material: 'mg', color: RACK_COLORS['Satin Black'] }
    } else {
      // Normal RML colors get a black nameplate
      materials.main = { name: 'metal', material: 'mg', color: 'black' }
    }

    return materials
  }
  
  // backOverride/topCrossmemberOverride: see rmMaterialStrategy.ts's version of this method for why backOverride must be checked instead of trusting this.config.back.
  createBackCrossmemberMaterials(backOverride?: RackOptions['back'], topCrossmemberOverride?: MaterialInfo): MaterialInfo {
    const backSelection = backOverride ?? this.config.back
    const topCrossmember = topCrossmemberOverride ?? this.config.materials?.topCrossmember

    // If explicitly set, use that
    if (backSelection === 'crossmember top 4' && topCrossmember) {
      return {
        main     : topCrossmember.main,
        hardware : { material: this.config.hardware },
      }
    }

    // Default rear crossmember materials
    return {
      main     : { material: 'mg', color: this.config.color },
      hardware : { material: this.config.hardware },
    }
  }
  
  //  RML back stabilizers are always powdercoat
  createBackStabilizerMaterials(): MaterialInfo {
    return {
      main     : { material: 'powdercoat' },
      hardware : { material: this.config.hardware },
    }
  }
  
  // frontCrossmemberOverride: see rmMaterialStrategy.ts's createBackCrossmemberMaterials for why the effective value is resolved here rather than read off this.config.
  createFrontCrossmemberMaterials(frontCrossmemberOverride?: MaterialInfo): MaterialInfo {
    const frontCrossmember = frontCrossmemberOverride ?? this.config.materials?.frontCrossmember
    const baseMaterials = super.createFrontCrossmemberMaterials(frontCrossmemberOverride)

    // An explicit Front Crossmember: selection wins - base has already resolved it, and RML's default below would otherwise overwrite the chosen color.
    if (frontCrossmember) return baseMaterials

    // RML can have a powdercoat front crossmember
    if (this.config.front?.includes('crossmember top 4')) {
      if (this.config.color === 'powdercoat') {
        baseMaterials.main = { name: 'metal', material: 'powdercoat' }
      } else {
        baseMaterials.main = { name: 'metal', material: 'mg' }
      }
    }
    
    return baseMaterials
  }
}